'use client';

import { useEffect, useMemo, useState, type FC } from 'react';
import { Button, ButtonGroup, Flex, Input, Separator } from '@/_components';
import type { AddDocumentSectionProps } from './AddDocumentSection.types';
import { Question } from '@/_types/models/questionnaire';

export const AddDocumentSection: FC<AddDocumentSectionProps> = ({
  isLoading,
  setDocuments,
  selectedPages = [],
  onClearSelection,
  questionnaire,
}) => {
  const [newDocument, setNewDocument] = useState('');
  const [isAddingDocument, setIsAddingDocument] = useState(false);
  const [results, setResults] = useState<Array<any>>([]);
  const [newDifferentiator, setNewDifferentiator] = useState('');

  const allUploadQuestions: Array<Question> = useMemo(
    () =>
      questionnaire?.questionnaire_questions?.filter(
        (q: Question) => q.question_type === 'upload',
      ) || [],
    [questionnaire?.questionnaire_questions],
  );

  const suggestions: Array<string> = useMemo(
    () =>
      allUploadQuestions.flatMap((question: Question) => {
        return question.options || [];
      }),
    [allUploadQuestions],
  );

  const parseDocumentOption = (
    option: string,
  ): { name: string; differentiator: string } => {
    // "W-2 (Soraban)" -> name: "W-2", differentiator: "Soraban"
    const match = option.match(/^(.+?)\s*\((.+?)\)$/);

    return {
      name: match?.[1]?.trim() ?? option.trim(),
      differentiator: match?.[2]?.trim() ?? '',
    };
  };

  useEffect(() => {
    setResults(
      suggestions.map((suggestion: string) => ({
        name: suggestion,
        value: suggestion,
      })),
    );
  }, [suggestions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex isColumn={true} gap={10}>
      <Separator isLight={true} />
      {isAddingDocument ? (
        <Flex gap={4} isColumn={true}>
          <Input
            label="Document name"
            placeholder="Search for document name"
            onChange={(event: any) => {
              setNewDocument(event.target.value);
              setResults(
                suggestions
                  .filter((suggestion: any) =>
                    suggestion
                      .toLowerCase()
                      .includes(event.target.value.toLowerCase()),
                  )
                  .map((suggestion: any) => ({
                    name: suggestion,
                    value: suggestion,
                  })),
              );
            }}
            onResultSelect={(result: any) => setNewDocument(result.name)}
            results={results}
            type="text"
            value={newDocument}
          />
          <Input
            label="Differentiator (Optional)"
            placeholder="Employer / Payer"
            onChange={(e: any) => setNewDifferentiator(e.target.value)}
            type="text"
            value={newDifferentiator}
          />
          <ButtonGroup align="space-between" isSameLine isFullWidth>
            <Button
              intent="danger-secondary"
              isDisabled={isLoading}
              onClick={() => {
                setIsAddingDocument(false);
                setNewDocument('');
                setNewDifferentiator('');
              }}
              value="Cancel"
            />
            <Button
              intent="secondary"
              iconLeft="plus"
              isDisabled={isLoading}
              onClick={() => {
                const { name, differentiator } =
                  parseDocumentOption(newDocument);
                const finalDifferentiator = (
                  newDifferentiator ||
                  differentiator ||
                  ''
                ).trim();
                const sortedPages = Array.from(new Set(selectedPages)).sort(
                  (a, b) => a - b,
                );
                setDocuments((prev: any[]) => {
                  const toMove = new Set(sortedPages);
                  const cleaned = prev.map((d: any) => {
                    const existing = Array.isArray(d.pages) ? d.pages : [];
                    const filtered = existing.filter(
                      (p: number) => !toMove.has(p),
                    );
                    if (filtered.length === existing.length) return d;
                    return {
                      ...d,
                      pages: filtered,
                      pageRanges: filtered.length ? filtered.join(', ') : '',
                    };
                  });
                  return [
                    ...cleaned,
                    {
                      name,
                      differentiator: finalDifferentiator,
                      pageRanges: sortedPages.join(', '),
                      pages: sortedPages,
                    },
                  ];
                });

                setNewDocument('');
                setNewDifferentiator('');
                setIsAddingDocument(false);
                onClearSelection?.();
              }}
              value="Add"
            />
          </ButtonGroup>
        </Flex>
      ) : (
        <ButtonGroup align="space-between" isSameLine={true} isFullWidth={true}>
          <Button
            intent="secondary"
            iconLeft="plus"
            isDisabled={isLoading}
            onClick={() => setIsAddingDocument(true)}
            value={`Add Document${selectedPages && selectedPages.length ? ` (${selectedPages.length} selected page${selectedPages.length > 1 ? 's' : ''})` : ''}`}
          />
        </ButtonGroup>
      )}
    </Flex>
  );
};
